import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && !user.force_password_reset) navigate("/", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await login(email, password);
      if (data.force_password_reset) navigate("/change-password", { replace: true });
      else navigate("/");
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #004d66 0%, #006080 50%, #008099 100%)" }}>
        <img src="https://images.unsplash.com/photo-1761888857629-aa3f306f6bee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHBhbGUlMjBnZW9tZXRyaWMlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NjAwNzI3OXww&ixlib=rb-4.1.0&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-overlay" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3"><div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center"><span className="text-white font-black text-lg">X</span></div><span className="font-bold text-xl tracking-tight">ExcelToAPI</span></div>
          <div>
            <h1 className="text-4xl font-black tracking-tight leading-tight mb-4">Transform Spreadsheets<br />into API Calls</h1>
            <p className="text-white/70 text-lg max-w-sm">Upload Excel or CSV files, define transformations, map to REST APIs, and execute at scale — no code required.</p>
          </div>
        </div>
      </div>
      <div className="flex-1 lg:max-w-md flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-sm fade-in">
          <div className="mb-8"><h2 className="text-2xl font-bold text-gray-900 tracking-tight">Sign in</h2><p className="text-gray-500 text-sm mt-1">Enter your credentials to continue</p></div>
          {error && <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="login-email" className="bg-white border-gray-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Password</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" className="bg-white border-gray-200" /></div>
            <Button type="submit" className="w-full bg-[#006080] hover:bg-[#004d66] text-white font-semibold h-10" disabled={loading} data-testid="login-submit">{loading ? "Signing in..." : "Sign in"}</Button>
          </form>
          <p className="mt-4 text-center text-sm text-gray-500"><Link to="/forgot-password" className="text-[#006080] hover:underline">Forgot password?</Link></p>
          <p className="mt-2 text-center text-sm text-gray-500">Don't have an account? <Link to="/register" className="text-[#006080] font-semibold hover:underline">Create account</Link></p>
        </div>
      </div>
    </div>
  );
}
